import React, { useState, useMemo, Fragment, useEffect } from 'react';
import { observer } from 'mobx-react';
import Modal from 'react-modal';
import Popover from '@mui/material/Popover';
import Link from '@mui/material/Link';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import AssessmentQuestionPreview from '../../../containers/assessment/AssessmentQuestionPreview';
import { TextDefault, TextLead } from '@awarego-ui/components';
import PreviewModalHeader from '../../../containers/previewModalHeader';
import PreviewQuestionDetails from './preview-question-details';
import QuestionPreviewTabbedContent from './QuestionPreviewTabbedContent';
import { useStores } from '../../../hooks/use-stores';
import { useTranslation } from 'react-i18next';
import './preview-question.scss';
import { refreshI18nPreview } from '../../../utils/refreshI18nPreview';
import {
    getStoredHraPreviewLanguage,
    resolveHraPreviewLanguage,
    setStoredHraPreviewLanguage,
} from '../../../utils/hraPreviewLanguage';

function QuestionPreviewModal({ question, companyId, onClose }) {
    const { i18n } = useTranslation('assessment');
    const { store, brandingStore } = useStores();
    const partnerId = companyId ? null : store.currentCompany?.account_id;
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState(() =>
        getStoredHraPreviewLanguage(
            (question.languages && question.languages[0]) || 'en'
        )
    );
    const [translationOverridesReady, setTranslationOverridesReady] =
        useState(!question.customizable);

    const previewPayload = useMemo(() => {
        const ui =
            question.ui ||
            question.definition?.ui || {
                blocks: [],
                presentation: null,
                minimumBlockCount: 1,
            };
        const id = question.id != null ? String(question.id) : 'preview-draft';
        return {
            ...question,
            id,
            ui,
            definition: {
                ...(question.definition || {}),
                ui,
            },
        };
    }, [question]);

    const customizeSaveTo = companyId
        ? 'company'
        : previewPayload.customizeSaveTo || 'company';

    const detailsQuestion = useMemo(
        () => ({
            ...previewPayload,
            behaviours: previewPayload.behaviours || [],
            adminDescription: previewPayload.adminDescription || '',
            difficulty: previewPayload.difficulty || 'basic',
        }),
        [previewPayload]
    );

    const questionDetails = useMemo(
        () => (
            <>
                <Link
                    component="button"
                    type="button"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                    <TextDefault themed bold flex gap={2} link>
                        <InfoIcon fontSize="small" />
                        Scenario details
                    </TextDefault>
                </Link>
                <TextLead>{previewPayload.name}</TextLead>
            </>
        ),
        [previewPayload.name]
    );

    useEffect(() => {
        const langs = previewPayload.languages || ['en'];
        const resolved = resolveHraPreviewLanguage(langs, langs[0]);
        void i18n.changeLanguage(resolved);
        setSelectedLanguage(resolved);
    }, [previewPayload.id, previewPayload.languages, i18n]);

    useEffect(() => {
        if (!previewPayload.customizable) {
            setTranslationOverridesReady(true);
            return;
        }
        let cancelled = false;
        setTranslationOverridesReady(false);
        (async () => {
            await brandingStore.ensureQuestionTranslationOverrides({
                partnerId,
                companyId,
                customizeSaveTo,
            });
            if (!cancelled) {
                setTranslationOverridesReady(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [
        previewPayload.customizable,
        customizeSaveTo,
        partnerId,
        companyId,
        brandingStore,
    ]);

    const handleSelectLanguage = async (languageKey) => {
        setSelectedLanguage(languageKey);
        setStoredHraPreviewLanguage(languageKey);
        await i18n.changeLanguage(languageKey);
        if (previewPayload.customizable && translationOverridesReady) {
            await refreshI18nPreview();
        }
    };

    return (
        <Modal
            isOpen
            onRequestClose={onClose}
            className="preview-questions-modal"
            overlayClassName="preview-questions-overlay"
            shouldCloseOnOverlayClick
        >
            <Fragment>
                <div className="question-preview-shell">
                    <PreviewModalHeader
                        titlePrefix="Preview:"
                        title={previewPayload.name}
                        showLanguageSelector
                        availableLanguages={previewPayload.languages || ['en']}
                        onClose={onClose}
                        onSelectLanguage={handleSelectLanguage}
                        selectedLanguage={selectedLanguage}
                        details={questionDetails}
                    />
                    <QuestionPreviewTabbedContent
                        customizable={Boolean(previewPayload.customizable)}
                        translationOverridesReady={translationOverridesReady}
                        language={selectedLanguage}
                        companyId={companyId}
                        partnerId={partnerId}
                        customizeSaveTo={customizeSaveTo}
                        definition={previewPayload}
                        questionId={previewPayload.id}
                        questionName={previewPayload.name}
                        previewContent={
                            <AssessmentQuestionPreview
                                companyId={companyId}
                                question={previewPayload}
                                isReview={false}
                                questionsSelected={[previewPayload.id]}
                                selectedQuestionId={previewPayload.id}
                                setSelectedQuestionId={() => {}}
                                hasMoreQuestions={false}
                                isMovingBack={false}
                                setIsMovingBack={() => {}}
                                onRequestClose={onClose}
                                loadingData={false}
                                isWizard={false}
                                hideFooter
                            />
                        }
                    />
                </div>
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    elevation={1}
                >
                    <PreviewQuestionDetails
                        handleQuestionDetailsClose={() => setAnchorEl(null)}
                        question={detailsQuestion}
                    />
                </Popover>
            </Fragment>
        </Modal>
    );
}

export default observer(QuestionPreviewModal);
